import React, { ReactNode } from "react";
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from "@mui/material";
import { Theme, ThemeProvider } from "@emotion/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@rentablo/api";
import { MandatorContextProvider } from "@rentablo/theming";
import { useLocalizedTheme, useMandatorContext } from "@rentablo/theming";

import { ErrorBoundary } from "@components";
import { SnackbarProvider } from "@providers";
import { SecurityImagesProvider } from "@providers";
import { SharedModalProvider } from "@providers/SharedModalProvider.tsx";
import { getDefaultMandatorSlug } from "@helpers";
import { ApiProvider } from "@providers/ApiProvider.tsx";
import { useMandatorSpecificTranslations } from "./hooks/useMandatorSpecificTranslations.tsx";
import { useMandatorFonts } from "./hooks/useMandatorFonts.tsx";

export const ThemeProviderWrapper = ({ children }: { children: ReactNode }) => {
  const { theme } = useLocalizedTheme();
  const { mandatorSlug } = useMandatorContext();
  useMandatorSpecificTranslations();
  useMandatorFonts(mandatorSlug);

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme as Partial<Theme>}>
          {children}
        </ThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
};

export const AppWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ErrorBoundary>
      <ApiProvider>
        <QueryClientProvider client={queryClient}>
          <SecurityImagesProvider>
            <MandatorContextProvider
              defaultMandatorSlug={getDefaultMandatorSlug()}
            >
              <ThemeProviderWrapper>
                <SharedModalProvider>
                  <SnackbarProvider>{children}</SnackbarProvider>
                </SharedModalProvider>
              </ThemeProviderWrapper>
            </MandatorContextProvider>
          </SecurityImagesProvider>
        </QueryClientProvider>
      </ApiProvider>
    </ErrorBoundary>
  );
};
